import React, { useMemo } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { FormTextField } from "../../../components/atoms";
import {
  Groups as GroupsIcon,
  MedicalServices as MedicalServicesIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  RestartAlt as RestartAltIcon,
  Toll as TollIcon,
} from "@mui/icons-material";
import { COLORS, TYPO, CARD_PAD, dashboardSectionSx } from "../../../styles/uiConstants";
import { SectionCard } from "../../../components/molecules";
import { ActionButton } from "../../../components/atoms";

/**
 * Modül bazlı token fiyatlandırması. Alanlar KONUYA GÖRE gruplanır ve her
 * modülün ne zaman token harcadığı satırın altında açıklanır — düz 16 satırlık
 * liste hangi ismin neyi kapsadığını göstermiyordu.
 *
 * Grup listesi tek kaynak: buradaki `key`'ler TokenTab'daki
 * MODULE_PRICING_FIELDS ile birebir aynı olmalı.
 */
export const PRICING_GROUPS = [
  {
    title: "Hasta ve Randevu",
    icon: <GroupsIcon />,
    description: "Hasta kaydı ve randevu akışında harcanan tokenlar",
    fields: [
      { key: "patients", label: "Hasta İşlemleri", defaultValue: 3, hint: "Hasta ekleme, güncelleme, kliniğe bağlama" },
      { key: "appointments", label: "Randevu İşlemleri", defaultValue: 3, hint: "Randevu oluşturma, erteleme, iptal" },
      { key: "pendingUsers", label: "Bekleyen Kullanıcı", defaultValue: 2, hint: "Başvuru onaylama ve reddetme" },
      { key: "users", label: "Kullanıcı İşlemleri", defaultValue: 3, hint: "Personel/hasta hesap işlemleri" },
    ],
  },
  {
    title: "Muayene Süreci",
    icon: <MedicalServicesIcon />,
    description: "Hasta dosyasına yazılan tıbbi kayıtlar",
    fields: [
      { key: "examinations", label: "Muayene İşlemleri", defaultValue: 3, hint: "Muayene oturumu başlatma ve tamamlama" },
      { key: "anamnez", label: "Anamnez İşlemleri", defaultValue: 2, hint: "Hasta öyküsü kaydı" },
      { key: "tetkik", label: "Tetkik İşlemleri", defaultValue: 3, hint: "Tetkik isteme ve sonuç girişi" },
      { key: "treatments", label: "Tedavi İşlemleri", defaultValue: 3, hint: "Reçete ve tedavi planı" },
      { key: "phoneCalls", label: "Telefon Görüşmesi", defaultValue: 2, hint: "Hasta arama kaydı" },
    ],
  },
  {
    title: "Tanım ve Şablonlar",
    icon: <DescriptionIcon />,
    description: "Klinik içi tanım listeleri ve hazır şablonlar",
    fields: [
      { key: "medicines", label: "İlaç Tanımı", defaultValue: 2, hint: "Klinik ilaç listesi düzenleme" },
      { key: "diseases", label: "Hastalık", defaultValue: 2, hint: "Tanı/hastalık tanımları" },
      { key: "diet", label: "Diyet", defaultValue: 2, hint: "Diyet planı oluşturma" },
      { key: "doctorTemplates", label: "Doktor Şablonları", defaultValue: 2, hint: "Doktora özel hazır şablonlar" },
      { key: "roleTemplates", label: "Rol Şablonları", defaultValue: 2, hint: "Personel yetki şablonları" },
    ],
  },
  {
    title: "Sistem",
    icon: <SettingsIcon />,
    description: "Klinik genelinde çalışan otomatik işlemler",
    fields: [
      { key: "notifications", label: "Bildirim", defaultValue: 2, hint: "Push/e-posta bildirim gönderimi" },
      { key: "schedule", label: "Takvim / Program", defaultValue: 2, hint: "Çalışma takvimi düzenleme" },
    ],
  },
];

// Fiyat satırı: kart içinde çizgiyle ayrılmış düz satır (iç kutu yok). Varsayılandan
// farklı değer tema renginde yazılır ve yanında "varsayılana dön" düğmesi çıkar.
function PricingRow({ field, value, onChange, canEdit, palette }) {
  const isCustom = value !== "" && value !== undefined && value !== field.defaultValue;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: CARD_PAD,
        py: 1.25,
        minHeight: 56,
        borderBottom: `1px solid ${COLORS.slate100}`,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: TYPO.body, fontWeight: 500, color: COLORS.slate800 }} noWrap>
          {field.label}
        </Typography>
        <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }} noWrap>
          {field.hint}
        </Typography>
      </Box>

      {isCustom && canEdit ? (
        <Tooltip title={`Varsayılana dön (${field.defaultValue})`} arrow>
          <IconButton size="small" aria-label={`${field.label} varsayılana dön`} onClick={() => onChange(field.defaultValue)}>
            <RestartAltIcon sx={{ fontSize: 16, color: COLORS.slate400 }} />
          </IconButton>
        </Tooltip>
      ) : null}

      <FormTextField
        palette={palette}
        type="number"
        variant="standard"
        size="small"
        value={value === undefined || value === null ? "" : value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!canEdit}
        placeholder={String(field.defaultValue)}
        inputProps={{ min: 0, "aria-label": `${field.label} token bedeli`, style: { textAlign: "right" } }}
        InputProps={{
          disableUnderline: !canEdit,
          endAdornment: (
            <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate400, ml: 0.75 }}>token</Typography>
          ),
          sx: {
            fontSize: TYPO.body,
            fontWeight: isCustom ? 600 : 400,
            color: isCustom ? palette.primary : COLORS.slate800,
            fontVariantNumeric: "tabular-nums",
          },
        }}
        sx={{ width: 96, flexShrink: 0 }}
      />
    </Box>
  );
}

/**
 * @param {object} editedPrices  - { [moduleKey]: number|"" }
 * @param {Function} onPriceChange - (key, rawValue) => void
 */
export default function TokenPricingCard({
  editedPrices = {},
  onPriceChange,
  canEdit = false,
  saving = false,
  onSave,
  palette,
}) {
  const customCount = useMemo(
    () =>
      PRICING_GROUPS.flatMap((g) => g.fields).filter((f) => {
        const v = editedPrices[f.key];
        return v !== "" && v !== undefined && v !== f.defaultValue;
      }).length,
    [editedPrices]
  );

  return (
    <SectionCard
      title="Token Tüketim Şablonu"
      subtitle="Bir modüldeki tüm işlemler aynı bedeli paylaşır; boş alan varsayılanı kullanır"
      icon={<TollIcon sx={{ fontSize: 18 }} />}
      sx={{ ...dashboardSectionSx, display: "flex", flexDirection: "column" }}
      action={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: TYPO.caption, color: customCount > 0 ? palette.primary : COLORS.slate500, whiteSpace: "nowrap" }}>
            {customCount > 0 ? `${customCount} modül özelleştirildi` : "Tümü varsayılan"}
          </Typography>
          {canEdit ? <ActionButton variant="save" compact palette={palette} loading={saving} onClick={onSave} /> : null}
        </Box>
      }
    >
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" }, columnGap: 0 }}>
        {PRICING_GROUPS.map((group, gi) => (
          <Box
            key={group.title}
            sx={{
              minWidth: 0,
              borderLeft: { xl: gi % 2 === 1 ? `1px solid ${COLORS.slate100}` : "none" },
            }}
          >
            <Box sx={{ px: CARD_PAD, pt: 2, pb: 0.75, borderBottom: `1px solid ${COLORS.slate100}` }}>
              <Typography
                sx={{
                  fontSize: TYPO.caption,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: COLORS.slate500,
                }}
              >
                {group.title}
              </Typography>
              <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate400 }}>{group.description}</Typography>
            </Box>
            {group.fields.map((field) => (
              <PricingRow
                key={field.key}
                field={field}
                value={editedPrices[field.key]}
                onChange={(raw) => onPriceChange?.(field.key, raw)}
                canEdit={canEdit}
                palette={palette}
              />
            ))}
          </Box>
        ))}
      </Box>
    </SectionCard>
  );
}
